import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { format } from "date-fns";
import { CheckCircle, Loader2 } from "lucide-react";
import React from "react";
import {
  usePaymentChannelChoices,
  usePaymentMode,
  usePaymentPayTypeChoices,
} from "../../hooks";

interface PaymentSummaryProps {
  consumer: any | null;
  formData: Record<string, any>;
  onCancel: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
  currency: string;
  pendingBills?: any[];
}

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  consumer,
  formData,
  onCancel,
  onConfirm,
  isSubmitting = false,
  currency,
  pendingBills = [],
}) => {
  const { data: paymentChannel } =
    usePaymentChannelChoices(getRemoteUtilityId());
  const { data: paymentType } = usePaymentPayTypeChoices(getRemoteUtilityId());
  const { data: paymentModes } = usePaymentMode(getRemoteUtilityId());

  const getPaymentLabel = (key: string | number) =>
    paymentType?.result?.find((item: any) => String(item.key) === String(key))
      ?.value ?? "-";

  const getChannelLabel = (code: string | number) =>
    paymentChannel?.result?.find(
      (item: any) => String(item.code) === String(code),
    )?.name ?? "-";

  const getPaymentModeLabel = (code: string) => {
    const paymentModeList = Array.isArray(paymentModes)
      ? paymentModes
      : Array.isArray(paymentModes?.result)
        ? paymentModes.result
        : [];
    return (
      paymentModeList.find((mode: any) => mode.code === code)?.name ?? code
    );
  };

  if (!consumer) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Payment Summary
        </h2>
        <p className="text-sm text-gray-500">
          Select a consumer to view payment summary
        </p>
      </div>
    );
  }

  const billAmount = Number(formData.amount) || 0;
  const creditApplied = Number(formData.creditApplied) || 0;
  const totalPayable = Number(formData.totalPayableAmount) || 0;
  const lateFee = Number(formData.lateFee) || 0;
  const isLateFeeWaived = formData.isLateFeeWaived || false;

  const isMultiBill =
    formData.paymentType === "2" && formData.selectedBills?.length > 1;
  const selectedBillObjects = pendingBills.filter((b) =>
    formData.selectedBills?.includes(b.invoiceNo),
  );

  const amountReceived = Number(
    formData.totalPayment ||
      formData.amountReceived ||
      formData.cashReceived ||
      formData.amount ||
      0,
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-6 mt-2">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">
        Payment Summary
      </h2>

      {/* Consumer Details */}
      <div className="mb-6">
        <p className="text-sm text-gray-800 font-medium mb-2">
          Consumer Details
        </p>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <ValueLabel
              value={consumer.firstName + " " + consumer.lastName}
              label="Name"
            />
            <ValueLabel value={consumer.consumerNo} label="Account No" />
          </div>

          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
            Credit: {currency} {consumer.creditBalance}
          </Badge>
        </div>
      </div>

      {/* Payment Channel/Type */}
      {(formData.paymentChannel || formData.paymentType) && (
        <div className="mb-6">
          <p className="text-xs font-medium text-gray-500 mb-2">
            Payment Channel / Type
          </p>
          <p className="text-sm text-gray-900">
            {getChannelLabel(formData.paymentChannel)} /{" "}
            {getPaymentLabel(formData.paymentType)}
          </p>
        </div>
      )}

      {/* Payment Mode */}
      {formData.paymentMode && (
        <div className="mb-6">
          <p className="text-xs font-medium text-gray-500 mb-2">Payment Mode</p>
          <p className="text-sm text-gray-900">
            {getPaymentModeLabel(formData.paymentMode)}
          </p>
        </div>
      )}

      {/* Payment Linked To (if applicable) */}
      {formData.linkedBillId && (
        <div className="mb-6">
          <p className="text-xs font-medium text-gray-500 mb-2">
            Payment Linked To
          </p>
          <p className="text-sm text-blue-600 font-medium">
            {formData.linkedBillId}
          </p>
        </div>
      )}

      {!isMultiBill && (
        <div className="space-y-2 mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total Payable Amount</span>
            <span className="font-medium text-gray-900">
              {currency} {formData.totalPayableAmount}
            </span>
          </div>

          <div className="flex justify-between text-sm mt-2">
            <span className="text-gray-600">Cash Received</span>
            <span className="font-medium text-gray-900">
              {currency} {formData.cashReceived}
            </span>
          </div>

          {formData.cashReceived - formData.totalPayableAmount > 0 && (
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-600">Refund</span>
              <span className="font-medium text-green-500">
                {currency}{" "}
                {(formData.cashReceived - formData.totalPayableAmount).toFixed(
                  2,
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Bill Amount Breakdown */}
      {billAmount > 0 && !isMultiBill && (
        <div className="mb-6 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Bill Amount</span>
            <span className="font-medium text-gray-900">
              {currency} {billAmount}
            </span>
          </div>

          {/* Late Fee */}
          {lateFee > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Late Fee</span>
              <span
                className={`font-medium ${
                  isLateFeeWaived
                    ? "line-through text-gray-400"
                    : "text-gray-900"
                }`}
              >
                {currency} {lateFee}
                {isLateFeeWaived && (
                  <Badge className="ml-2 bg-green-100 text-green-800 text-xs">
                    Waived
                  </Badge>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {isMultiBill && selectedBillObjects.length > 0 && (
        <div className="mb-6">
          <p className="text-[11px] font-bold text-gray-400 mb-3 uppercase tracking-wider">
            BILLS SELECTED ({selectedBillObjects.length})
          </p>

          <div className="border border-gray-200 rounded-xl p-3 bg-white mb-6">
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {selectedBillObjects.map((bill) => {
                const bLateFee = bill.lateFee || 0;
                const billStatus = String(bill.billStatus || "").toUpperCase();
                const isBOverdue = billStatus === "OVERDUE";
                const isBWaived = formData.isLateFeeWaived || false;

                return (
                  <div
                    key={bill.id}
                    className="border border-gray-200 rounded-lg p-3 flex justify-between items-start"
                  >
                    <div>
                      <p className="font-bold text-gray-900 text-[13px] mb-1.5">
                        {bill.invoiceNo}
                      </p>
                      <Badge
                        className={`px-2 py-0.5 text-[10px] font-medium border ${
                          isBOverdue
                            ? "bg-red-50 text-red-600 border-red-200"
                            : billStatus === "GENERATED"
                              ? "bg-blue-50 text-blue-600 border-blue-200"
                              : "bg-yellow-50 text-yellow-700 border-yellow-200"
                        }`}
                      >
                        {bill.billStatus}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900 text-[13px]">
                        {currency} {bill.outstandingAmount}
                      </p>
                      {isBOverdue && bLateFee > 0 && (
                        <p
                          className={`text-[11px] mt-1 font-medium ${isBWaived ? "text-gray-400 line-through" : "text-red-500"}`}
                        >
                          +{currency} {bLateFee} late fee
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between text-[13px]">
              <span className="text-gray-500">Bills Subtotal</span>
              <span className="font-bold text-gray-900">
                {currency}{" "}
                {selectedBillObjects
                  .reduce((acc, b) => acc + Number(b.outstandingAmount || 0), 0)
                  .toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between text-[13px] pb-4 border-b border-gray-200 border-dashed">
              <span className="text-gray-500">Late Fees</span>
              <span
                className={`font-bold ${isLateFeeWaived ? "line-through text-gray-400" : "text-red-600"}`}
              >
                {currency} {lateFee.toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between text-[13px] font-bold pt-1">
              <span className="text-gray-900">Subtotal</span>
              <span className="text-gray-900">
                {currency} {billAmount.toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between text-[14px] font-bold mt-1">
              <span className="text-gray-900">Total Payable Amount</span>
              <span className="text-blue-600">
                {currency} {totalPayable?.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Credit Applied */}
      {creditApplied > 0 && (
        <div className="mb-6 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Credit Applied</span>
            <span className="font-medium text-green-600">
              - {currency} {creditApplied}
            </span>
          </div>
        </div>
      )}

      {/* Total Payable Amount */}
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-1">Total Payable Amount</p>
        <p className="text-xl font-bold text-gray-900">
          {currency} {totalPayable}
        </p>
      </div>

      {/* Payment Date & Recorded By */}
      <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
        <div>
          <p className="text-gray-500 mb-1">Payment Date</p>
          <p className="text-gray-900">
            {formData.paymentDate
              ? format(new Date(formData.paymentDate), "M/d/yyyy")
              : format(new Date(), "M/d/yyyy")}
          </p>
        </div>
        <div>
          <p className="text-gray-500 mb-1">Recorded By</p>
          <p className="text-gray-900">{formData.recordedBy || "User"}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <div>
          <Button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="w-full bg-green-500 hover:bg-green-600 text-white disabled:bg-green-400"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            {isSubmitting ? "Processing..." : "Confirm Payment"}
          </Button>
          <p className="text-xs text-center text-gray-500">
            Review details and confirm
          </p>
        </div>
        <Button
          onClick={onCancel}
          variant="outline"
          disabled={isSubmitting}
          className="w-full"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
